<template>
  <div :class="$b({ inLensGuide, singleLine: singleLineSwatches })">
    <div
      v-for="(partItem, idx) in part.groupParts"
      :key="partItem.name"
      :class="$b('container')"
    >
      <div
        v-if="!singleLineSwatches && (!firstRow || partItem.name === 'Bottom')"
        :class="
          $b('spacer', {
            tabsOnDesktop,
            noHeight:
              (bottomFollowFrame && partItem.name === 'Bottom') ||
              (iconsFollowLens && partItem.name === 'Icons') ||
              (logosFollowLens && partItem.name === 'Logos') ||
              (frameFollowStrap && partItem.name === 'Strap'),
          })
        "
      />

      <div
        v-if="showHeading"
        :class="$b('heading')"
      >
        <div :class="$b('choice-name')">
          <h4
            :class="$b('choice-text')"
          >
            <template v-if="partItem.name === 'Frame'">
              <!-- eslint-disable-next-line vue/no-v-html -->
              <span
                :class="$b('heading-part', { hasFrameOptions, mobile: true })"
              >
                {{ 'Top' }}:
              </span>
            </template>
            <template v-if="partItem.name === 'Bottom'">
              <!-- eslint-disable-next-line vue/no-v-html -->
              <span
                :class="$b('heading-part', { hasFrameOptions, mobile: true })"
              >
                {{ 'Bottom' }}:
              </span>
            </template>
            <span
              :class="$b('heading-part', { hasFrameOptions, desktop: true })"
            >
              {{ $tr(partItem.name) }}
            </span>
            <!-- eslint-disable vue/no-v-html -->
            <span
              :class="$b('subheading-part')"
              v-html="currentChoiceName[idx]"
            />
            <!-- eslint-enable vue/no-v-html -->
          </h4>
        </div>

        <div
          v-if="
            hasAfterChoiceNameSlotContent ||
              hasRecycledPart ||
              partItem.name === 'Bottom'
          "
          :class="$b('after-choice-name-slot')"
        >
          <slot
            name="afterChoiceName"
          />
          <PartLinkButton
            v-if="
              partItem.name === 'Bottom' &&
                !isCkOrParts &&
                showBottomFollowFrame
            "
            :class="$b('link-button')"
            :is-linked="bottomFollowFrame"
            @click="$emit('toggle-bottom-frame-link')"
          >
            {{ $tr('Match to Top Frame') }}
          </PartLinkButton>
        </div>

        <div
          v-if="hasHeadingSlotContent"
          :class="$b('heading-slot')"
        >
          <slot />
        </div>
      </div>

      <div
        v-if="
          partItem.name === 'Bottom' ||
            (partItem.name === 'Icons' && isMdBreakpointOrAbove) ||
            (partItem.name === 'Logos' && isMdBreakpointOrAbove) ||
            (partItem.name === 'Frame' && isMdBreakpointOrAbove)
        "
        :class="$b('choices')"
      >
        <FadeSlideDownTransition is-grid-elem>
          <ChoiceSelect
            v-show="
              (!bottomFollowFrame && partItem.name === 'Bottom') ||
                (!iconsFollowLens && partItem.name === 'Icons') ||
                (!logosFollowLens && partItem.name === 'Logos') ||
                (!frameFollowStrap && partItem.name === 'Frame')
            "
            shape="circle"
            full-width
            :choices="filteredChoices[idx]"
            :disable-infinite-swatch-icon="partItem.disableInfiniteSwatchIcon"
            :part="partItem"
            :customised-product="customisedProduct"
            :single-line-swatches="singleLineSwatches"
            @select="(choice) => handleChoiceSelect(choice)"
          >
            <div
              v-if="hasAfterSlotContent"
              :class="
                $b('after-slot', {
                  singleLineSwatches,
                })
              "
            >
              <slot name="after" />
            </div>
          </ChoiceSelect>
        </FadeSlideDownTransition>
      </div>

      <div
        v-else
        :class="$b('choices')"
      >
        <ChoiceSelect
          v-if="
            (!iconsFollowLens && partItem.name === 'Icons') ||
              (!logosFollowLens && partItem.name === 'Logos') ||
              (!frameFollowStrap && partItem.name === 'Frame') ||
              (partItem.name !== 'Bottom' &&
                partItem.name !== 'Icons' &&
                partItem.name !== 'Logos' &&
                partItem.name !== 'Frame')
          "
          shape="circle"
          full-width
          :choices="filteredChoices[idx]"
          :part="partItem"
          :customised-product="customisedProduct"
          :disable-infinite-swatch-icon="partItem.disableInfiniteSwatchIcon"
          :single-line-swatches="singleLineSwatches"
          @select="(choice) => handleChoiceSelect(choice)"
        >
          <div
            v-if="hasAfterSlotContent"
            :class="
              $b('after-slot', {
                singleLineSwatches,
              })
            "
          >
            <slot name="after" />
          </div>
        </ChoiceSelect>
      </div>
      <div
        v-if="partItem.name === 'Lenses' &&
          customisedProduct.isPrescriptionSunglassesAvailable &&
          customisedProduct.product.pageMode !== 'lenses'
        "
        :class="$b('prescription-toggle')"
      >
        <VisionLink
          :is-button="true"
          :line-link="true"
          :mono-font="true"
          theme="grey"
          :class="$b('prescription-toggle-text')"
          @click.prevent="handlePrescriptionToggleClick()"
        >
          <template v-if="!prescriptionMode"> Prescription Lenses </template>
          <template v-else> Non-Prescription Lenses </template>
        </VisionLink>
      </div>
    </div>
  </div>
</template>

<script>
import CustomisedProduct from '~/models/CustomisedProduct'
import lensIconPairings from '@/assets/js/constants/lensIconPairings'
import { testSlot } from '@/assets/js/helpers/slots'

export default {
  name: 'CustomiserRow',

  props: {
    customisedProduct: { type: CustomisedProduct, required: true },
    part: { type: Object, required: true },
    inLensGuide: { type: Boolean, default: false },
    iconsFollowLens: { type: Boolean, default: false },
    logosFollowLens: { type: Boolean, default: false },
    bottomFollowFrame: { type: Boolean, default: false },
    frameFollowStrap: { type: Boolean, default: false },
    firstRow: { type: Boolean, default: false },
    tabsOnDesktop: { type: Boolean, default: false },
    singleLineSwatches: { type: Boolean, default: false },
    showHeading: { type: Boolean, default: true },
    prescriptionMode: { type: Boolean, default: false },
  },

  emits: [
    'toggle-bottom-frame-link',
    'select',
    'unlink-icons-and-lenses',
    'unlink-bottom-and-frame',
    'unlink-frame-and-strap',
    'hide-icon-lens-link',
    'show-icon-lens-link',
    'hide-frame-strap-link',
    'show-frame-strap-link',
    'prescription-mode-toggled'
  ],

  setup (_, { slots }) {
    const modalStore = useModalStore()

    const hasHeadingSlotContent = computed(() => {
      return testSlot(slots.default)
    })

    const hasAfterSlotContent = computed(() => {
      return testSlot(slots.after)
    })

    const hasAfterChoiceNameSlotContent = computed(() => {
      return testSlot(slots.afterChoiceName)
    })

    const { isMdBreakpointOrAbove } = useBreakpoint()

    return {
      openModal (slug) {
        modalStore.OPEN_MODAL_PAGE({ slug })
      },
      hasHeadingSlotContent,
      hasAfterSlotContent,
      hasAfterChoiceNameSlotContent,
      isMdBreakpointOrAbove,
    }
  },

  data() {
    return {
      showBottomFollowFrame: true,
    }
  },

  computed: {
    lensIconPairings() {
      return lensIconPairings
    },
    currentChoice() {
      return this.part.groupParts.map((groupPart) => {
        const partSkuPrefix = groupPart.stockManagedSKUPrefix
        if (partSkuPrefix in this.customisedProduct.allChoices)
          return this.customisedProduct.allChoices[partSkuPrefix]
        return null
      })
    },
    currentChoiceName() {
      return this.currentChoice.map((choice) => {
        return !this.customisedProduct.allowLensTechFiltering ||
          this.customisedProduct.lensTechSelected
          ? choice?.name
          : choice?.nameWithoutLensTech
      })
    },
    filteredChoices() {
      return this.part.groupParts?.map((part) =>
        part.getFilteredOptions(this.customisedProduct)
      )
    },
    hasFrameOptions() {
      return this.part.groupName === 'frame' && this.part.groupParts.length > 1
    },
    showRecycledText() {
      return Object.values(this.customisedProduct.choices).some(
        (choice) => choice?.isRecycled
      )
    },
    hasIcons() {
      return this.customisedProduct.product.parts.find(
        (part) => part.name === 'Icons'
      )
    },
    iconChoiceSkus() {
      if (this.hasIcons)
        return this.hasIcons.options
          .filter((option) => option.quantity > 0)
          .map((icon) => icon.sku)
      return null
    },
    hasLogos() {
      return this.customisedProduct.product.parts.find(
        (part) => part.name === 'Logos'
      )
    },
    logoChoiceSkus() {
      if (this.hasLogos)
        return this.hasLogos.options
          .filter((option) => option.quantity > 0)
          .map((logo) => logo.sku)
      return null
    },
    bottomChoiceSkus() {
      if (this.hasFrameOptions)
        return this.customisedProduct.product.parts
          .find((part) => part.name === 'Bottom')
          .options.filter((option) => option.quantity > 0)
          .map((bottom) => bottom.sku)
      return null
    },
    availableFrameSkus() {
      return this.customisedProduct.product.parts
        .find((part) => part.name === 'Frame')
        .options.filter((option) => option.quantity > 0)
        .map((frame) => frame.sku)
    },
    availableStrapSkus() {
      if (this.part.groupName === 'strap')
        return this.customisedProduct.product.parts
          .find((part) => part.name === 'Strap')
          .options.filter((option) => option.quantity > 0)
          .map((strap) => strap.sku)
      return null
    },
    hasRecycledPart() {
      return this.currentChoice.find((choice) => choice?.isRecycled)
    },
    isCkOrParts() {
      return ['parts', 'conversionkit'].includes(
        this.customisedProduct.product.pageMode
      )
    },
    frameColours() {
      const frameOptions = this.customisedProduct.partsWithChoice.filter(
        (part) => part.name === 'Frame'
      )[0]?.options
      if (!frameOptions) return []
      return frameOptions
        .map((option) => {
          let colourFromSku = option.sku.match(/f_([a-z]*)?(M)?/)
          if (!colourFromSku[1])
            colourFromSku = option.sku.match(/f_[M|I|F]?([a-z]*)?/)
          const colour = colourFromSku[1]
          return colour
        })
        .sort((a, b) => b.length - a.length)
    },
  },

  watch: {
    iconsFollowLens(newValue) {
      if (newValue && this.part.groupName === 'lenses' && this.hasIcons) {
        const lensChoice = this.currentChoice.find(
          (choice) => choice.partType === 'lenses'
        )

        this.handleChoiceSelect(lensChoice)
      }
    },
    logosFollowLens(newValue) {
      if (newValue && this.part.groupName === 'lenses' && this.hasLogos) {
        const lensChoice = this.currentChoice.find(
          (choice) => choice.partType === 'lenses'
        )

        this.handleChoiceSelect(lensChoice)
      }
    },
    bottomFollowFrame(newValue) {
      if (newValue && this.part.groupName === 'frame') {
        const frameChoice = this.currentChoice.find(
          (choice) => choice.partType === 'frame'
        )

        this.handleChoiceSelect(frameChoice)
      }
    },
    frameFollowStrap(newValue) {
      if (newValue && this.part.groupName === 'strap') {
        const strapChoice = this.currentChoice.find(
          (choice) => choice.partType === 'strap'
        )

        this.handleChoiceSelect(strapChoice)
      }
    },
    hasFrameOptions(newValue) {
      if (
        newValue &&
        this.part.groupName === 'frame' &&
        this.bottomFollowFrame
      ) {
        const frameChoice = this.currentChoice.find(
          (choice) => choice.partType === 'frame'
        )

        this.handleChoiceSelect(frameChoice)
      }
    },
    currentChoice(newValue) {
      this.checkStock(newValue)
    },
  },
  mounted() {
    if(this.currentChoice) {
      this.checkStock(this.currentChoice)
    }
  },
  methods: {
    handleChoiceSelect(choice) {
      const choices = {
        mainPart: choice,
        followerPart: {
          sku: null,
        },
      }
      if (choice.partType === 'lenses' && this.iconsFollowLens && this.hasIcons)
        choices.followerPart.sku = this.getIconSku(choice)
      if (choice.partType === 'lenses' && this.logosFollowLens && this.hasLogos)
        choices.followerPart.sku = this.getLogoSku(choice)
      if (
        choice.partType === 'frame' &&
        this.bottomFollowFrame &&
        this.hasFrameOptions
      )
        choices.followerPart.sku = this.getFrameBottomSku(choice.sku)
      if (choice.partType === 'strap' && this.frameFollowStrap)
        choices.followerPart.sku = this.getFrameSku(choice.sku)

      this.$emit('select', choices)
    },
    getAllIconPairSkus(lensColour, currentIcon) {
      return lensIconPairings[lensColour].map((colour) =>
        currentIcon.replace(/i_(B)?([a-z]*)/, `i_$1${colour}`)
      )
    },
    getAllLogoPairSkus(lensColour, currentLogo) {
      return lensIconPairings[lensColour].map((colour) =>
        currentLogo.replace(/ll_(B)?([a-z]*)/, `ll_$1${colour}`)
      )
    },
    getIconSku(choice) {
      const lensColour = choice.skuWithoutLensTech.split('_')?.[1]
      const currentIcon = this.customisedProduct.choices?.i.sku
      const pairedIconColours = this.getAllIconPairSkus(lensColour, currentIcon)
      if (!this.iconChoiceSkus) {
        this.$emit('unlink-icons-and-lenses')
        return null
      }
      const targetIconSku = pairedIconColours.find((colour) =>
        this.iconChoiceSkus.includes(colour)
      )
      return targetIconSku
    },
    getLogoSku(choice) {
      const lensColour = choice.skuWithoutLensTech.split('_')?.[1]
      const currentLogo = this.customisedProduct.choices?.ll.sku
      const pairedLogoColours = this.getAllLogoPairSkus(lensColour, currentLogo)
      if (!this.logoChoiceSkus) {
        this.$emit('unlink-icons-and-lenses')
        return null
      }
      const targetLogoSku = pairedLogoColours.find((colour) =>
        this.logoChoiceSkus.includes(colour)
      )
      return targetLogoSku
    },
    getFrameBottomSku(frameSku) {
      const topSkuParts = frameSku.match(/f_(M|I|F)?([a-z]*)?/)
      const topColour = topSkuParts[2]
      const topSkuPrefix = topSkuParts[1]
      const choices = Object.values(this.customisedProduct.choices)
      const secondaryChoices = Object.values(
        this.customisedProduct.secondaryChoices
      )
      let currentBottomChoice = choices.find(
        (choice) => choice.partType === 'bottom'
      )
      if (!currentBottomChoice)
        currentBottomChoice = secondaryChoices.find(
          (choice) => choice.partType === 'bottom'
        )
      const newBottomChoice = currentBottomChoice.sku.replace(
        /b_(M|I|F)?([a-z]*)/,
        `b_${topSkuPrefix}${topColour}`
      )
      const newBottomChoiceInStock =
        this.bottomChoiceSkus?.includes(newBottomChoice)
      if (!newBottomChoiceInStock) {
        this.$emit('unlink-bottom-and-frame')
        return null
      }
      return newBottomChoice
    },
    getFrameSku(strapSku) {
      const strapSkuParts = strapSku.match(/s_([0-9A-z]*)?/)
      const strapColour = strapSkuParts[1]
      let newFrameColour = this.frameColours?.find((colour) =>
        strapColour.startsWith(colour)
      )
      if (!newFrameColour && strapColour === 'katieo') newFrameColour = 'pink'
      else if (!newFrameColour && strapColour === 'woodsy')
        newFrameColour = 'black'
      else if (!newFrameColour && strapColour === '1ftp') newFrameColour = 'white'
      else if (!newFrameColour) newFrameColour = 'black'
      const choices = Object.values(this.customisedProduct.choices)
      const currentFrameChoice = choices.find(
        (choice) => choice.partType === 'frame'
      )
      const newFrameChoice = currentFrameChoice?.sku.replace(
        /f_([a-z]*)?(M)?/,
        `f_${newFrameColour}$2`
      )
      const newFrameChoiceInStock =
        this.availableFrameSkus?.includes(newFrameChoice)
      if (!newFrameChoiceInStock) {
        this.$emit('unlink-frame-and-strap')
        return null
      }
      return newFrameChoice
    },
    checkStock(partToCheck) {
      if (this.customisedProduct.isPrebuiltVariant) return

      if (this.part.groupName === 'strap') {
        const potentialLinkedFrame = partToCheck[0] && this.getFrameSku(partToCheck[0].sku)
        if (!potentialLinkedFrame) {
          this.$emit('hide-frame-strap-link')
        } else {
          this.$emit('show-frame-strap-link')
        }
      }
      if (this.part.groupName === 'frame' && this.hasFrameOptions) {
        const potentialLinkedBottom = partToCheck[0] && this.getFrameBottomSku(partToCheck[0].sku)
        if (!potentialLinkedBottom) {
          this.showBottomFollowFrame = false
        } else {
          this.showBottomFollowFrame = true
        }
      }
      if (
        this.part.groupName === 'lenses' &&
        (this.hasIcons || this.hasLogos)
      ) {
        let potentialLinkedIconOrLogo
        if (this.hasIcons)
          potentialLinkedIconOrLogo = partToCheck[0] && this.getIconSku(partToCheck[0])
        if (this.hasLogos)
          potentialLinkedIconOrLogo = partToCheck[0] && this.getLogoSku(partToCheck[0])

        if (!potentialLinkedIconOrLogo) {
          this.$emit('hide-icon-lens-link')
        } else {
          this.$emit('show-icon-lens-link')
        }
      }
    },
    async handlePrescriptionToggleClick() {
      this.$emit('prescription-mode-toggled', !this.prescriptionMode)

      await this.$track.global.linkClicked({
        interactionElement: 'prescription-toggle',
        interactionSection: 'Customiser'
      }, 'Prescription Mode Toggle Clicked')
    }
  },
}
</script>

<style lang="postcss">
.CustomiserRow {
  @apply w-full;

  .ProductCustomiser--parts-mode & {
    @apply pt-xs;
  }
}

.CustomiserRow__container {
  @apply flex flex-col mt-xxs;
}

.CustomiserRow__spacer {
  @apply hidden;

  @screen md {
    @apply block;

    height: theme('spacing.sm');
    transition: height 0.3s ease-out;

    &--tabs-on-desktop {
      height: theme('spacing.sm');
    }
  }

  &--no-height {
    height: 0;
  }
}

.CustomiserRow__heading {
  @apply flex items-baseline justify-between mb-2
  mdMax:order-3 mdMax:pt-2;

  min-height: 25px;
}

.CustomiserRow__heading-part {
  @apply hidden md:inline mr-2;

  &--has-frame-options {
    @apply inline;

    &.CustomiserRow__heading-part--mobile {
      @apply inline md:hidden;
    }

    &.CustomiserRow__heading-part--desktop {
      @apply hidden md:inline;
    }
  }

  &--mobile {
    @apply md:hidden;
  }
}

.CustomiserRow__subheading-part {
  @apply text-black font-fragment-mono text-xxs;
}

.CustomiserRow__choice-text {
  @apply text-xs font-bold;

  .CustomiserRow--in-lens-guide & {
    @screen xs {
      @apply text-sm;
    }

    @screen xl {
      @apply text-base;
    }
  }
}

.CustomiserRow__part-name {
  padding-right: calc(theme('spacing.xxxs') / 2);
}

.CustomiserRow__after-choice-name-slot {
  @apply ml-xxxs lg:ml-xs
    flex flex-col items-end justify-start;
}

.CustomiserRow__heading-slot {
  @apply ml-xxxs lg:ml-xs;
}

.CustomiserRow__after-slot {
  @apply w-full col-span-5 flex-shrink-0;
}

.CustomiserRow__info-button {
  @apply text-[12px] whitespace-nowrap;

  .CustomiserRow__link-button + & {
    margin-top: 5px;
  }
}

.CustomiserRow--single-line .CustomiserRow__choices {
  @apply -mx-xxs;
}

.CustomiserRow__prescription-toggle {
  @apply md:mt-3 flex flex-wrap gap-y-2 justify-between order-4 md:order-1;
}

.CustomiserRow__prescription-toggle-text {
  @apply text-tiny;
}
</style>
